import CryptoJS from 'crypto-js';
import { safeConsole } from '../utils/productionUtils';

// Get the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// SECURITY: Encryption key for tokens - MUST be set in production environment
const getEncryptionKey = () => {
  const key = process.env.REACT_APP_TOKEN_KEY;
  if (!key) {
    throw new Error('SECURITY ERROR: REACT_APP_TOKEN_KEY environment variable is required for token encryption. Never use hardcoded keys in production.');
  }
  return key;
};

/**
 * Token Service - Handles secure token management in the frontend
 */
class TokenService {
  /**
   * SECURITY: Encrypt data before storing in localStorage
   * @param {string} data - Data to encrypt
   * @returns {string} Encrypted data
   */
  _encrypt(data) {
    try {
      const key = getEncryptionKey();
      return CryptoJS.AES.encrypt(data, key).toString();
    } catch (error) {
      safeConsole.error('Encryption error:', error);
      return data; // Fallback to unencrypted for backward compatibility
    }
  }

  /**
   * SECURITY: Decrypt data from localStorage
   * @param {string} encryptedData - Encrypted data to decrypt
   * @returns {string} Decrypted data
   */
  _decrypt(encryptedData) {
    try {
      const key = getEncryptionKey();
      const bytes = CryptoJS.AES.decrypt(encryptedData, key);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      return decryptedData || encryptedData; // Fallback if decryption fails
    } catch (error) {
      safeConsole.error('Decryption error:', error);
      return encryptedData; // Return original data if decryption fails
    }
  }

  /**
   * SECURITY: Secure localStorage operations with encryption
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   */
  _secureSetItem(key, value) {
    try {
      const encryptedValue = this._encrypt(value);
      localStorage.setItem(key, encryptedValue);
    } catch (error) {
      safeConsole.error('Secure storage error:', error);
      // Fallback to regular localStorage for backward compatibility
      localStorage.setItem(key, value);
    }
  }

  /**
   * SECURITY: Secure localStorage retrieval with decryption
   * @param {string} key - Storage key
   * @returns {string|null} Decrypted value or null
   */
  _secureGetItem(key) {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) return null;
      return this._decrypt(encryptedValue);
    } catch (error) {
      safeConsole.error('Secure retrieval error:', error);
      return localStorage.getItem(key); // Fallback
    }
  }
  /**
   * Get the current access token from storage
   * @returns {string|null} The access token or null if not found
   */
  getAccessToken() {
    try {
      const tokenData = this._secureGetItem('auth_tokens');
      if (tokenData) {
        const { accessToken } = JSON.parse(tokenData);
        if (accessToken) return accessToken;
      }
      return null;
    } catch (error) {
      safeConsole.error('Error getting access token:', error);
      return null;
    }
  }

  /**
   * Get the current refresh token from storage
   * @returns {string|null} The refresh token or null if not found
   */
  getRefreshToken() {
    try {
      // SECURITY: Use encrypted storage
      const tokenData = this._secureGetItem('auth_tokens');
      if (!tokenData) return null;
      
      const { refreshToken } = JSON.parse(tokenData);
      return refreshToken || null;
    } catch (error) {
      safeConsole.error('Error getting refresh token:', error);
      return null;
    }
  }

  /**
   * Save tokens to local storage
   * @param {Object} tokens - Object containing accessToken, refreshToken, and expiresAt
   */
  saveTokens({ accessToken, refreshToken, expiresAt }) {
    try {
      // SECURITY: Use encrypted storage for tokens
      const tokenData = JSON.stringify({
        accessToken,
        refreshToken,
        expiresAt,
        timestamp: Date.now() // Add timestamp for additional security tracking
      });
      this._secureSetItem('auth_tokens', tokenData);
      
      // SECURITY: Clear any legacy unencrypted tokens
      localStorage.removeItem('token');
    } catch (error) {
      safeConsole.error('Error saving tokens:', error);
    }
  }

  /**
   * SECURITY: Clear all token storage (both encrypted and legacy)
   */
  clearTokens() {
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('token'); // Clear legacy tokens too
    safeConsole.log('🔒 All authentication tokens cleared');
  }

  /**
   * Validate the current token with the backend
   * @returns {Promise<Object>} Object containing validation result
   */
  async validateToken() {
    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        return { success: false, error: 'No token found' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/validate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: accessToken })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      safeConsole.error('Token validation error:', error);
      return { success: false, error: 'Token validation failed' };
    }
  }

  /**
   * Refresh the access token using the refresh token
   * @returns {Promise<Object>} Object containing new tokens or error
   */
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return { success: false, error: 'No refresh token found' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      const data = await response.json();
      
      if (data.success) {
        // Save the new tokens
        this.saveTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresAt: data.expiresAt
        });
      }
      
      return data;
    } catch (error) {
      safeConsole.error('Token refresh error:', error);
      return { success: false, error: 'Token refresh failed' };
    }
  }

  /**
   * Get authorization header with bearer token
   * @returns {Object} Headers object with Authorization
   * @throws {Error} If no token is available
   */
  getAuthHeader() {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('No token provided');
    }
    return { 'Authorization': `Bearer ${token}` };
  }

  /**
   * Check if the token is about to expire and refresh if needed
   * @returns {Promise<boolean>} True if token is valid (refreshed if needed)
   */
  async ensureValidToken() {
    try {
      const validation = await this.validateToken();
      
      if (!validation.success) {
        // Token is invalid, try to refresh
        const refresh = await this.refreshToken();
        return refresh.success;
      }
      
      if (validation.tokenInfo?.isExpiringSoon) {
        // Token is expiring soon, refresh it
        const refresh = await this.refreshToken();
        return refresh.success;
      }
      
      return true;
    } catch (error) {
      safeConsole.error('Error ensuring valid token:', error);
      return false;
    }
  }
}

export default new TokenService(); 